import React from "react";
import { ChevronDown, Coins, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MapleOSLogo from "@/components/common/MapleOSLogo";
import UserActions from "@/components/layout/UserActions";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useAICredits } from "@/hooks/useAICredits";

type NavItem = {
  label: string;
  to: string;
  external?: boolean;
};

const exploreLinks: NavItem[] = [
  { label: "Welcome", to: "/#hero-slider" },
  { label: "Living System", to: "/#a-living-system" },
  { label: "Try MapleOS", to: "/#try-mapleos" },
  { label: "Builders", to: "/#built-for-builders" },
  { label: "App Ecosystem", to: "/#current-app-ecosystem" },
];

const productLinks: NavItem[] = [
  { label: "MapleOS 26", to: "/products/mapleos-26" },
  { label: "MapleOS Pro 26", to: "/products/mapleos-pro-26" },
  { label: "MapleOS Enterprise 26", to: "/products/mapleos-enterprise-26" },
];

const communityLinks: NavItem[] = [
  { label: "News", to: "/news" },
  { label: "Forums", to: "/forums" },
  { label: "Bugs", to: "/bugs" },
  { label: "Partnership", to: "/partnership" },
  { label: "GitHub", to: "https://github.com/MapleOSAI", external: true },
];

type LandingNavbarProps = {
  showSectionLinks?: boolean;
};

type DropdownNavProps = {
  label: string;
  items: NavItem[];
};

function DropdownNav({ label, items }: DropdownNavProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-white/65 outline-none ring-0 transition-colors duration-200 hover:text-white focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
        >
          {label}
          <ChevronDown className="h-4 w-4 text-white/45" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        alignOffset={-6}
        sideOffset={10}
        className="min-w-[11.5rem] w-auto rounded-2xl border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.98),rgba(7,12,20,0.98))] p-1 text-white shadow-[0_24px_80px_rgba(0,0,0,0.42)]"
      >
        {items.map((item) => (
          <DropdownMenuItem
            key={item.label}
            asChild
            className="rounded-xl px-2.5 py-1 text-white/78 focus:bg-white/10 focus:text-white"
          >
            {item.external ? (
              <a
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full whitespace-nowrap text-right font-medium text-white"
              >
                {item.label}
              </a>
            ) : (
              <Link to={item.to} className="block w-full whitespace-nowrap text-right font-medium text-white">
                {item.label}
              </Link>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function LandingNavbar({ showSectionLinks = true }: LandingNavbarProps) {
  const { user } = useAuth();
  const { account, loading } = useAICredits();

  return (
    <motion.nav
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-background/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-6 gap-y-3 px-4 py-4 sm:px-6">
        <Link to="/" className="group flex shrink-0 items-center gap-2" aria-label="mapleos home">
          <MapleOSLogo className="h-8 w-auto text-foreground transition-colors duration-300 group-hover:text-primary" />
        </Link>

        {showSectionLinks ? (
          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/65">
            <DropdownNav label="Explore" items={exploreLinks} />
            <DropdownNav label="Products" items={productLinks} />
            <DropdownNav label="Community" items={communityLinks} />
            <Link to="/docs" className="whitespace-nowrap transition-colors duration-200 hover:text-white">
              Docs
            </Link>
          </div>
        ) : null}

        <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-3">
          {user ? (
            <>
              <Link
                to="/credits"
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/10"
                title="View and buy AI credits"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />}
                <span>Credits</span>
                <span className="text-white/70">{account.availableCredits.toLocaleString()}</span>
              </Link>
              <UserActions variant="landing" />
            </>
          ) : (
            <UserActions variant="landing" className="text-sm text-white/70 transition-colors hover:text-white" />
          )}

          <Link
            to="/downloads"
            className="inline-flex rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/5"
          >
            Download OS
          </Link>
          <Link
            to="/start"
            className="inline-flex rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-[0_0_24px_hsl(var(--primary)/0.25)] transition-transform duration-200 hover:scale-[1.03] active:scale-[0.97]"
          >
            Launch mapleos
          </Link>
        </div>
      </div>
    </motion.nav>
  );
}